import {createContext, ReactNode, useCallback, useEffect, useReducer, useRef, useState} from "react";
import {
  Error, Session, User, GetBalanceRequestSchema, ImageLoginRequestSchema, OtpLoginRequestSchema,
  EmailLoginRequestSchema, RegisterUserRequestSchema, GetUserRequestSchema, type GetUserReply
} from "../../generated/smg_service_pb";
import {dateFormat, isFullSwing, Partner_Client, PlayerBaySession_Client, SMG_Client} from "../constants";
import {
  EvenPlayAuthRequestSchema, FullSwingAuthRequestSchema, OtpCredentialSchema, ImageCredentialSchema
} from "../../generated/player_bay_session_pb";
import {FullSwingTokenLoginRequestSchema} from "../../generated/partner_service_pb";
import {isStationNotBaySessionManaged} from "../grpc/stationNotManaged";
import {
  LeaveSessionRequestSchema, AutoPlayBalanceStatus as BaySessionAutoPlayBalanceStatus, PlayerReadiness, PlayerState,
  type SessionPlayer, WagerMode
} from "../../generated/bay_session_shared_pb";
import {AutoPlayBalanceStatus} from "../../generated/smg_service_pb";
import {CancelModalProps, ExtendedUserDataType, IContestEntryModal, registerUserPayloadType} from "../types";
import {identifyUser, resetUser, track} from "../services/analytics";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {Buffer} from "buffer";
import * as Crypto from "expo-crypto";
import moment from 'moment';
import ServiceClient from "../utils/ServiceClient";
import {markExplicitPartnerSignOut} from "../utils/partnerSignOutGate";
import {clearPartnerRemovalGate} from "../utils/partnerRemovalGate";
import MainStyle from "../../MainStyle";
import LevelUpModal from "../modals/LevelUpModal";
import {PartnerKioskUpdate_UserSessionUpdateData} from "../../generated/partner_service_pb";
import {create} from "@bufbuild/protobuf";
import {isForceNewPlayer} from "../utils/devOverrides";
import {formatCurrency} from "../utils";
import {
  bayPlayerProjectionReducer, type BayPlayerProjectionSnapshot, type BayPlayerProjectionState,
  type BayPlayerProjectionStatus, createInitialBayPlayerProjection
} from '../state/bayPlayerProjection';
import type {RoundRobinStatus} from '../utils/roundRobinProjection';
import {persistBayPlayerJwt, readBayPlayerJwt} from '../services/bayPlayerCredentialStorage';
import {
  clearFullSwingAuthSession, DEFAULT_SESSION_BACKEND, FULL_SWING_SESSION_ID_STORAGE_KEY, persistFullSwingAuthSession,
  SESSION_BACKEND_STORAGE_KEY, type SessionBackend
} from '../services/fullSwingAuthSessionStorage';
import {AUTH_ERROR_MESSAGES, SafeAuthError, normalizeAuthError} from '../auth/authErrorPolicy';
import {reportAuthFailure} from '../services/authErrorReporter';

export type {RoundRobinStatus} from '../utils/roundRobinProjection';
export type {SessionBackend} from '../services/fullSwingAuthSessionStorage';
export {DEFAULT_SESSION_BACKEND, SESSION_BACKEND_STORAGE_KEY} from '../services/fullSwingAuthSessionStorage';

const defaultCancelModalConfig = {visible: false, title: '', subtitle: ''};
const LEGACY_STATION_ID_STORAGE_KEY = 'stationId';

const readPersistedLegacyStationId = async (): Promise<number | null> => {
  try {
    const raw = await AsyncStorage.getItem(LEGACY_STATION_ID_STORAGE_KEY);
    if (!raw) return null;
    const stationId = Number(raw);

    return Number.isInteger(stationId) && stationId > 0 ? stationId : null;

  } catch (err) {
    console.log('AsyncStorage.getItem legacy stationId err', err);
    return null;
  }
};

const toPositiveBetId = (value: string | number | bigint | undefined): bigint | undefined => {
  if (value === undefined || value === '') return undefined;

  try {
    const betId = BigInt(value);
    return betId > 0n ? betId : undefined;

  } catch {
    return undefined;
  }
};

// SMG-3446: shallow element-wise equality for the WagerMode arrays bridged
// from SessionPlayer, so the stream dispatcher can skip value-equal setState
// calls (the per-user stream ticks sub-second).
function arraysEqual(a: readonly WagerMode[], b: readonly WagerMode[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export type BayPlayerCredentialStatus = 'hydrating' | 'ready' | 'missing' | 'error';

// Credential shape accepted by Context.evenPlayAuth. Mirrors the
// EvenPlayAuthRequest.credential oneof (OtpCredential | ImageCredential) but
// keeps the screen-facing API in plain strings — image bytes are converted
// from base64 (the shape a camera capture produces) inside evenPlayAuth,
// same conversion imageLogin already performs.
type EvenPlayOtpCredential = {
  kind: 'otp', contact: { method: 'email'; value: string } | { method: 'phone'; value: string }, code: string
};
type EvenPlayImageCredential = { kind: 'image', imageBase64: string };
type EvenPlayCredential = EvenPlayOtpCredential | EvenPlayImageCredential;
type EvenPlayAuthParams = { credential: EvenPlayCredential, sessionId: string, promoType?: string };

// Login eligibility gates shared by every consumer and FullSwing login path.
// Running the same function before any credential or identity side effect
// guarantees malformed/under-18 DOBs and token-less responses fail closed
// regardless of auth method or backend. `sessionToken` is passed by each caller
// so this boundary stays independent of the divergent RPC reply shapes.
const assertLoginEligibleOrThrow = (dateOfBirth: string | undefined, sessionToken: string | undefined) => {
  if (dateOfBirth) {
    const dob = moment(dateOfBirth, dateFormat);
    // Fail closed on an unparseable DOB: the old `diff(...) < 18` check returned
    // NaN for an invalid moment and `NaN < 18` is false, silently bypassing the
    // under-18 gate. Treat malformed and under-18 identically (reject).
    if (!dob.isValid() || dob.isAfter(moment().subtract(18, 'years'))) {
      throw new SafeAuthError({
        category: 'age_restricted', code: 'age_restricted', retryable: false,
        userMessage: AUTH_ERROR_MESSAGES.ageRestricted
      });
    }
  }

  if (!sessionToken) {
    // smg-core LoginUser is fail-closed on both paths, so production always has
    // a token; empty only happens when smg-core is unwired in dev. Surface as
    // retryable rather than letting the next smgClient.* call fail.
    throw new SafeAuthError({
      category: 'service_unavailable', code: 'missing_session_token', retryable: true,
      userMessage: AUTH_ERROR_MESSAGES.serviceUnavailable
    });
  }
};

/**
 * The wire User plus app-local bookkeeping that must share the user's
 * lifecycle. lastAppliedEngineAutoPlayAmount is the edge-trigger marker
 * for the engine autoplay preference merge (in User.autoPlayAmount
 * encoding: -2 = always play max, > 0 = pinned cents; undefined until
 * the first projected frame is applied). It lives on the user object —
 * not in a ref — so the merge updater stays pure under React updater
 * replay and the marker is dropped with the user on logout; see
 * mergePlayerStreamProjections.
 */
export type AppUser = User & { lastAppliedEngineAutoPlayAmount?: number };

type BalanceSnapshot = { balance: number, totalBonuses: number };
type UserSnapshot = { user: AppUser, extendedUserData: ExtendedUserDataType, maxShotAmount: number };

const createUserSnapshot = (res: GetUserReply): UserSnapshot => {
  const {
    success, user, error, maxShotAmount, hasActiveTournament, freeShotCount, freeShotNumber, isNewPlayer, hasNewBonus,
    tournamentAutoPlayAmount, skillIndex
  } = res;

  if (!success || !user) {
    throw new SafeAuthError({
      category: 'profile_unavailable', code: error ? 'profile_response_error' : 'profile_missing', retryable: true,
      userMessage: AUTH_ERROR_MESSAGES.profileUnavailable
    });
  }

  return {
    user, maxShotAmount,
    extendedUserData: {
      maxShotAmount, hasActiveTournament, freeShotCount, freeShotNumber, skillIndex: Number(skillIndex),
      // GetUser increments freeShotNumber only while onboarding, so normalize
      // it to the raw completed-shot count shared with the live projection.
      completedFreeShots: isNewPlayer ? freeShotNumber - 1 : freeShotNumber,
      isNewPlayer: isNewPlayer || isForceNewPlayer(), hasNewBonus, tournamentAutoPlayAmount
    }
  };
};

function mergeStreamUserProjection(prev: AppUser, sp: SessionPlayer): AppUser {
  let autoPlayAmount = prev.autoPlayAmount;
  let lastAppliedEngineAutoPlayAmount = prev.lastAppliedEngineAutoPlayAmount;

  if (sp.engineAutoPlay) {
    const engineAmount = sp.engineAutoPlay.useMax ? -2 : Number(sp.engineAutoPlay.pinnedCents);
    if (lastAppliedEngineAutoPlayAmount !== engineAmount) {
      autoPlayAmount = engineAmount;
      lastAppliedEngineAutoPlayAmount = engineAmount;
    }

  } else if (sp.autoContinueWagerCents !== 0n) {
    autoPlayAmount = Number(sp.autoContinueWagerCents);
  }

  // SMG-4387: prefer the ENGINE flag over the roster projection.
  //
  // These are two different columns. sp.autoContinueEnabled mirrors
  // bay_session_players.auto_continue_enabled, which has a schema DEFAULT TRUE
  // that the insert deliberately omits — so a freshly-joined player reads
  // "autoplay on" regardless of what they actually chose. The engine flag
  // (sp.engineAutoPlay.enabled) is what gates the wallet debit.
  //
  // The divergence was harmless while the engine ignored its own flag for
  // SOLO and behaved as always-on, matching the TRUE projection. Now that the
  // engine honours consent, every genuine opt-out — the mid-session toggle,
  // and the balance-disable — produced a phone that said autoplay was ON while
  // the engine would not act on it. Worse, setAutoContinueInternal's no-op
  // pre-flight short-circuits when the roster column already holds the
  // requested value and never forwards to engine, so such a player could not
  // repair it by tapping "Start AutoPlay" — they had to toggle off, then on.
  //
  // The field was already on the wire and documented as "carried; not yet
  // consumed by mobile" (bay_session_shared.proto). It is consumed now. Fall
  // back to the roster column only when engine enrichment is absent, which is
  // the pre-existing behaviour.
  const autoPlayEnabled = sp.engineAutoPlay ? sp.engineAutoPlay.enabled : sp.autoContinueEnabled;

  const autoPlayBalanceStatus = sp.autoPlayBalanceStatus as unknown as AutoPlayBalanceStatus;
  if (prev.autoPlayEnabled === autoPlayEnabled && prev.autoPlayAmount === autoPlayAmount &&
    prev.lastAppliedEngineAutoPlayAmount === lastAppliedEngineAutoPlayAmount &&
    prev.autoPlayBalanceStatus === autoPlayBalanceStatus) {
    return prev;
  }

  return {
    ...prev, autoPlayEnabled, autoPlayAmount, lastAppliedEngineAutoPlayAmount,
    autoPlayBalanceStatus
  };
}

function mergeStreamExtendedUserProjection(prev: ExtendedUserDataType, sp: SessionPlayer): ExtendedUserDataType {
  // A zero freePlayCount is also the wire default when engine enrichment is
  // unavailable, so it cannot safely erase the getUser-sourced progress.
  const hasFreePlayProjection = sp.freePlayCount > 0;
  const streamMaxShotAmount = Number(sp.usdMaxWagerCents ?? 0n);
  const isNewPlayer = hasFreePlayProjection ? sp.isNewPlayer || isForceNewPlayer() : prev.isNewPlayer;
  const freeShotCount = hasFreePlayProjection ? sp.freePlayCount : prev.freeShotCount;
  const freeShotNumber = hasFreePlayProjection ? sp.completedFreePlayCount : prev.freeShotNumber;
  const completedFreeShots = hasFreePlayProjection ? sp.completedFreePlayCount : prev.completedFreeShots;
  const maxShotAmount = streamMaxShotAmount > 0 ? streamMaxShotAmount : prev.maxShotAmount;

  if (prev.isNewPlayer === isNewPlayer && prev.freeShotCount === freeShotCount &&
    prev.freeShotNumber === freeShotNumber && prev.completedFreeShots === completedFreeShots &&
    prev.skillIndex === sp.skillIndex && prev.maxShotAmount === maxShotAmount) {
    return prev;
  }

  return {
    ...prev, isNewPlayer, freeShotCount, freeShotNumber, completedFreeShots, skillIndex: sp.skillIndex, maxShotAmount
  };
}

type initTypes = {
  user: AppUser | undefined,
  getUser: (session?: Session) => Promise<User>,
  session: Session | undefined,
  setSession: (val: Session) => void,
  logout: () => Promise<boolean>,
  registerUser: (val: registerUserPayloadType) => Promise<Session>,
  emailLogin: (email: string, password: string) => Promise<true>,
  imageLogin: (val: string) => Promise<true>,
  cancelModalConfig: CancelModalProps,
  setCancelModalConfig: (val: CancelModalProps | ((prev: CancelModalProps) => CancelModalProps)) => void,
  loadingOverlay: boolean,
  setLoadingOverlay: (val: boolean) => void,
  getBalance: (session?: Session) => Promise<number | Error>,
  balance: number,
  // SMG-3250: lets the live PlayerState stream (Main.tsx) refresh the
  // displayed balance without the one-shot getBalance round-trip.
  setBalance: (val: number) => void,
  isSuccessMessageShow: string,
  setIsSuccessMessageShow: (message: string) => void
  contestEntryModalConfig: IContestEntryModal,
  setContestEntryModalConfig: (val: IContestEntryModal) => void,
  isTest: boolean,
  setIsTest: (val: boolean) => void,
  onLine: boolean,
  setOnLine: (val: boolean) => void,
  totalBonuses: number,
  otpLogin: (email: string, code: string) => Promise<true>,
  fullSwingTokenLogin: (session_id: string, id_token: string) => Promise<User>,
  // FSG-8881: re-run the FullSwing auth handshake after registration
  // (profile + T&C) completes. FullSwingAuth defers its implicit session
  // join while tnc_accepted=false, so a new user does not appear on the
  // in-game roster mid-signup; this re-run — with tnc now accepted —
  // performs the deferred join. No-op (resolved) when no FullSwing
  // deep-link login happened this app session (e.g. a mid-registration
  // page refresh dropped the in-memory credentials) — the user then
  // joins on their next QR scan instead.
  completeRegistrationJoin: () => Promise<void>,
  // Native evenplay self-check-in. Builds a real EvenPlayAuthRequest from the
  // given credential (OTP or facial image), calls the EvenPlayAuth RPC, and
  // runs the same fail-closed gate and transactional session commit as
  // fullSwingTokenLoginBaySession. Returns the committed User so the screen can
  // drive tnc-gated navigation exactly like fullSwingTokenLogin.
  evenPlayAuth: (params: EvenPlayAuthParams) => Promise<User>,
  // Bay-session player JWT issued by PlayerBaySessionService.FullSwingAuth
  // alongside the partner-service login. Required as the
  // Authorization: Bearer header on EVERY subsequent bay-session RPC
  // (StreamPlayerState, AcceptBet, GetBetDetails, SetAutoContinue, ...).
  // Empty until the FullSwingAuth handshake completes; useStreamPlayerState
  // checks for non-empty before opening the stream to avoid the 401
  // "token is empty" round-trip.
  bayPlayerJwt: string,
  bayPlayerCredentialStatus: BayPlayerCredentialStatus,
  setBayPlayerJwt: (jwt: string) => Promise<void>,
  retryBayPlayerCredential: () => Promise<void>,
  // Which backend the current session authenticated against. Drives
  // useLiveSessionStream's dispatch (bay-session projection vs legacy
  // passthrough) and is persisted/rehydrated so a cold start resumes the same
  // stream.
  sessionBackend: SessionBackend,
  setSessionBackend: (val: SessionBackend) => void,
  // Lifecycle of the current bay-session projection. `ready` is reached only
  // when Main atomically applies an authoritative PlayerState snapshot.
  bayPlayerProjectionStatus: BayPlayerProjectionStatus,
  bayPlayerProjectionReady: boolean,
  bayPlayerProjectionRetryKey: number,
  applyBayPlayerProjection: (snapshot: BayPlayerProjectionSnapshot) => void,
  markBayPlayerProjectionError: () => void,
  retryBayPlayerProjection: () => void,
  extendedUserData: ExtendedUserDataType,
  setUser: (val: AppUser | undefined) => void,
  // Merge the live autoplay / free-play / skill-index projections from
  // the bay-session per-user PlayerStateUpdate stream into
  // Context.user + Context.extendedUserData. Called from Main.tsx's
  // dispatcher on every PlayerStateUpdate frame; replaces the BetSlip
  // 10s `Context.getUser` poll.
  mergePlayerStreamProjections: (state: PlayerState | undefined) => void,
  stationId: number,
  setStationId: (val: number) => void,
  // SMG-3006: stream-driven active bet id from PlayerState.
  // SessionPlayer.active_bet_id. Empty string = "no active bet"; a
  // non-empty value (e.g. "8") is the canonical "BetSlip should
  // render bet 8 now" signal. Main.tsx populates from the per-user
  // bay-session stream on every frame; BetSlip listens for changes
  // and fires GetBets('accepted') once per change instead of polling.
  bayActiveBetId: string,
  // Concrete wallet token (USD / TGPTS / FSPTS / ...) of the active bet,
  // projected from SessionPlayer.wagers_by_currency in the atomic snapshot.
  // Empty when the active bet has no
  // bay_session_wagers row (the legacy FullSwing engine-accept path, which is
  // always USD). ResizeWager must be keyed on this — the ACTUAL wager's
  // currency — not the next-wager category picker (SMG-3492).
  bayActiveBetCurrency: string,
  // Live amount (minor units) of the active accepted wager, projected
  // atomically on every stream frame: from SessionPlayer.wagers_by_currency
  // when an accepted wager row exists (bay-native path), else from
  // SessionPlayer.active_bet_amount (legacy FullSwing engine-accept bets have
  // no wager row — without the fallback a ResizeWager never moved this value
  // and the paytable stayed stale at the pre-resize amount).
  // ResizeWager re-amounts the bet in place (same bet_id), so this is the only
  // stream signal that the displayed amount changed; BetSlip re-fetches the
  // active bet when it moves.
  // 0 = no active bet.
  bayActiveBetAmountMinor: number,
  // SMG-3481: server-authoritative per-shot arm state, mirrored from
  // PlayerState.shot_armed_bet_id on every stream frame. The shot is armed
  // for the active bet iff bayShotArmedBetId === active bet id. Survives a
  // reload (unlike the optimistic local latch), so the per-shot confirm
  // affordance reflects truth after a refresh.
  bayShotArmedBetId: string,
  // SMG-3484: server-authoritative per-player autoplay-balance status,
  // mirrored from SessionPlayer.auto_play_balance_status on every stream
  // frame (the same bridge pattern as bayActiveBetId / bayShotArmedBetId).
  // AUTO_PLAY_BALANCE_INSUFFICIENT means the player cannot fund the
  // next/current wager — FullSwingHomePage surfaces the insufficient-funds
  // notice off this even when an UNAFFORDABLE bet is already in the
  // 'accepted' state (bay-session debits at COMMIT, not accept, so
  // accepted != funded). Proto3 default is AUTO_PLAY_BALANCE_OK (treated as
  // "ok / engine-down unknown").
  bayAutoPlayBalanceStatus: BaySessionAutoPlayBalanceStatus,
  // Server-derived readiness for THIS player. CONFIRM means timeout,
  // guardian, or the opted-in walkaway gate is engaged. ConfirmReadyToSwing
  // clears the self-clearable gates; the popup routes on this so the tap
  // does not merely clear the independent per-turn gate.
  bayPlayerReadiness: PlayerReadiness,
  streamError: string,
  setStreamError: (val: string) => void,
  userSessionData: PartnerKioskUpdate_UserSessionUpdateData | undefined,
  setUserSessionData: (val: PartnerKioskUpdate_UserSessionUpdateData | undefined) => void,
  roundRobinStatus: RoundRobinStatus | null,
  setRoundRobinStatus: (val: RoundRobinStatus | null) => void,
  confirmationBypassEnabled: boolean,
  setConfirmationBypassEnabled: (enabled: boolean) => void,
  // SMG-3056: per-TURN confirmation flag (bay-session on_turn). Distinct from
  // confirmationBypassEnabled which now derives from on_shot (SMG-3048).
  // Driven by Main.tsx from PlayerState.session_player.confirmation.on_turn;
  // useConfirmBeforeEachTurn reads + writes this so the "Lock account after
  // each turn" toggle reflects the persisted on_turn (and the modal trigger
  // actually fires for players who opt in).
  confirmBeforeEachTurnEnabled: boolean,
  setConfirmBeforeEachTurnEnabled: (enabled: boolean) => void,
  // SMG-3446: the wager CATEGORIES the station offers this player —
  // SessionPlayer.allowed_wager_modes, bridged from the per-user
  // PlayerStateUpdate stream by Main.tsx. The mobile currency picker
  // (Real Money vs Points) reads this to decide whether to render and
  // which categories to offer; the single-availability rule hides the
  // picker when fewer than two currency categories are selectable.
  allowedWagerModes: WagerMode[],
  setAllowedWagerModes: (modes: WagerMode[]) => void,
  // SMG-3446: the player's current wager CATEGORY (SessionPlayer.wager_mode),
  // so the picker can reflect server truth (the active category) and decide
  // between SetWagerMode (no live wager) and SwitchWagerCurrency (live wager).
  activeWagerMode: WagerMode,
  setActiveWagerMode: (mode: WagerMode) => void,
  // SMG-3446: the active player's available USD balance in MINOR units (cents),
  // bridged from SessionPlayer.funds (CurrencyAvailability where currency=USD,
  // the `available` field). The currency picker's funds-aware default reads this
  // to decide whether to default to Real Money (>= $5) or move the player to
  // Points (< $5) with an informational banner. 0 when USD funds are absent
  // (proto3 default repeated / wallet projection not yet wired).
  usdAvailableMinor: number,
  setUsdAvailableMinor: (val: number) => void,
  // SMG-3455: server-driven funding-fallback signals from the per-user
  // PlayerStateUpdate stream (PlayerStateReason). These are AUTHORITATIVE —
  // the server decides whether the player's next wager could be funded.
  //
  // serverMovedToPointsInsufficientFunds: the server auto-switched the
  // player's next wager to Points because their real-money balance could not
  // fund the next bet (reason PLAYER_MOVED_TO_POINTS_INSUFFICIENT_FUNDS).
  // Drives the "we've moved you to Points" banner regardless of whether the
  // client-side USD<$5 heuristic (useWagerCurrency.autoMovedToPointsInsufficientUSD)
  // fired — the banner shows for EITHER trigger. Main.tsx sets it; a single
  // dismiss (dismissInsufficientUSDBanner) clears it.
  serverMovedToPointsInsufficientFunds: boolean,
  setServerMovedToPointsInsufficientFunds: (val: boolean) => void,
  // insufficientFundsNoFallback: the player cannot fund the next wager and
  // there is NO points fallback (reason PLAYER_INSUFFICIENT_FUNDS). No
  // auto-switch happened. Drives the NEW "out of funds" banner. Main.tsx sets
  // it; the banner's dismiss clears it.
  insufficientFundsNoFallback: boolean,
  setInsufficientFundsNoFallback: (val: boolean) => void,
  showProfileModal: boolean,
  setShowProfileModal: (val: boolean) => void,
  // Read-only visibility projection published by the confirmation controller.
  // FullSwingHomePage consumes it only to avoid stacking competing modals.
  // Zero means not visible; lifecycle and backend routing stay in the controller.
  activePlayerConfirmationModalVisible: number,
  setActivePlayerConfirmationModalVisible: (val: number) => void,
};

const defaultValue: initTypes = {
  user: undefined,
  getUser: (): any => {
  },
  session: undefined,
  setSession: () => {
  },
  logout: (): any => {
  },
  registerUser: (): any => {
  },
  emailLogin: (): any => {
  },
  imageLogin: (): any => {
  },
  cancelModalConfig: defaultCancelModalConfig,
  setCancelModalConfig: () => {
  },
  loadingOverlay: false,
  setLoadingOverlay: () => {
  },
  getBalance: (): any => {
  },
  balance: 0,
  setBalance: () => {
  },
  isSuccessMessageShow: '',
  setIsSuccessMessageShow: () => {
  },
  contestEntryModalConfig: {},
  setContestEntryModalConfig: () => {
  },
  isTest: false,
  setIsTest: () => {
  },
  onLine: true,
  setOnLine: () => {
  },
  totalBonuses: 0,
  otpLogin: (): any => {
  },
  extendedUserData: {
    maxShotAmount: 0, hasActiveTournament: false, freeShotCount: 0, freeShotNumber: 0, completedFreeShots: 0,
    isNewPlayer: false, hasNewBonus: false, tournamentAutoPlayAmount: 0, skillIndex: 0
  },
  setUser: () => {
  },
  mergePlayerStreamProjections: () => {
  },
  fullSwingTokenLogin: (): any => {
  },
  completeRegistrationJoin: (): any => {
  },
  evenPlayAuth: (): any => {
  },
  bayPlayerJwt: '',
  bayPlayerCredentialStatus: 'hydrating',
  setBayPlayerJwt: async () => {
  },
  retryBayPlayerCredential: async () => {
  },
  sessionBackend: DEFAULT_SESSION_BACKEND,
  setSessionBackend: () => {
  },
  bayPlayerProjectionStatus: 'idle',
  bayPlayerProjectionReady: false,
  bayPlayerProjectionRetryKey: 0,
  applyBayPlayerProjection: () => {
  },
  markBayPlayerProjectionError: () => {
  },
  retryBayPlayerProjection: () => {
  },
  stationId: 0,
  setStationId: (): any => {
    // bayActiveBetId default seeded in provider; identical no-op
    // pattern as setStationId for consistency.
  },
  bayActiveBetCurrency: '',
  bayActiveBetAmountMinor: 0,
  bayActiveBetId: '',
  bayShotArmedBetId: '',
  bayAutoPlayBalanceStatus: BaySessionAutoPlayBalanceStatus.AUTO_PLAY_BALANCE_OK,
  bayPlayerReadiness: PlayerReadiness.UNSPECIFIED,
  streamError: '',
  setStreamError: () => {
  },
  userSessionData: undefined,
  setUserSessionData: () => {
  },
  roundRobinStatus: null,
  setRoundRobinStatus: () => {
  },
  confirmationBypassEnabled: false,
  setConfirmationBypassEnabled: () => {
  },
  confirmBeforeEachTurnEnabled: false,
  setConfirmBeforeEachTurnEnabled: () => {
  },
  allowedWagerModes: [],
  setAllowedWagerModes: () => {
  },
  activeWagerMode: WagerMode.NONE,
  setActiveWagerMode: () => {
  },
  usdAvailableMinor: 0,
  setUsdAvailableMinor: () => {
  },
  serverMovedToPointsInsufficientFunds: false,
  setServerMovedToPointsInsufficientFunds: () => {
  },
  insufficientFundsNoFallback: false,
  setInsufficientFundsNoFallback: () => {
  },
  showProfileModal: false,
  setShowProfileModal: () => {
  },
  activePlayerConfirmationModalVisible: 0,
  setActivePlayerConfirmationModalVisible: () => {
  },
};

export const StoreContext = createContext(defaultValue);

type Props = { children: ReactNode };
export const StoreContextProvider = (props: Props) => {
  const {children} = props;
  const [user, setUser] = useState<AppUser>();
  const [session, setSession] = useState<Session | undefined>();
  const [cancelModalConfig, setCancelModalConfig] = useState<CancelModalProps>(defaultCancelModalConfig);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [balance, setBalance] = useState(0);
  const [isSuccessMessageShow, setIsSuccessMessageShow] = useState('');
  const [contestEntryModalConfig, setContestEntryModalConfig] = useState<IContestEntryModal>({visible: false});
  const [isTest, setIsTest] = useState(false);
  const client = SMG_Client;
  const [onLine, setOnLine] = useState(true);
  const [totalBonuses, setTotalBonuses] = useState(0);
  const [extendedUserData, setExtendedUserData] = useState<ExtendedUserDataType>({
    maxShotAmount: 0, hasActiveTournament: false, freeShotCount: 0, freeShotNumber: 0, completedFreeShots: 0,
    isNewPlayer: false, hasNewBonus: false, tournamentAutoPlayAmount: 0, skillIndex: 0
  });
  const [showLevelUpModal, setShowLevelUpModal] = useState(false);
  // Last maxShotAmount the user has been shown (cents). Ref, not state:
  // the level-up comparison must see the true previous value even when
  // two updates land in one render pass — the old pattern of comparing
  // against extendedUserData.maxShotAmount read a stale closure and
  // could both miss and double-fire the popup. 0 = not yet known
  // (pre-first-getUser); the first observation baselines silently.
  const knownMaxShotAmountRef = useRef(0);

  /**
   * Record a server-reported maxShotAmount (cents) and fire the
   * level-up popup on a genuine increase. Callers: getUser (login /
   * explicit refresh) and mergePlayerStreamProjections (bay-session
   * SessionPlayer.usd_max_wager_cents, the live mid-session source —
   * the BSA epic removed the 10s getUser poll, so without the stream
   * path an unlock earned mid-session never surfaced until re-login).
   * Ignores 0 ("unknown": old server or unpopulated projection).
   *
   * The latch is RAISE-ONLY: a lower observation never lowers the ref.
   * The two sources are async (a getUser response snapshotted before an
   * unlock can land AFTER the stream already delivered the new max);
   * lowering on a stale read would re-arm the latch and double-fire on
   * the next stream frame. Cost: an admin lowering a user's override
   * and later restoring it won't re-celebrate — the safe direction.
   * Reset to 0 only on logout (per-user latch).
   */
  const registerMaxShotAmount = useCallback((nextMax: number) => {
    if (!nextMax || nextMax <= 0) return;
    const prevMax = knownMaxShotAmountRef.current;
    if (nextMax <= prevMax) return;
    knownMaxShotAmountRef.current = nextMax;
    if (!prevMax) return;

    if (isFullSwing) {
      setShowLevelUpModal(true);

    } else {
      setCancelModalConfig({
        visible: true, title: 'Success', titleColor: MainStyle.c_green, leftBtnText: 'OK',
        subtitle: `Your Max Challenge Fee has increased to ${formatCurrency(nextMax / 100)}!\n\nYou can adjust your Challenge Fee in the app.`,
        onExit: () => setCancelModalConfig(prev => ({...prev, visible: false}))
      });
    }
  }, []);
  const [bayPlayerProjection, dispatchBayPlayerProjection] = useReducer(bayPlayerProjectionReducer, undefined, () => createInitialBayPlayerProjection());
  const {
    status: bayPlayerProjectionStatus, stationId, activeBetId: bayActiveBetId, activeBetCurrency: bayActiveBetCurrency,
    activeBetAmountMinor: bayActiveBetAmountMinor, shotArmedBetId: bayShotArmedBetId, userSessionData, roundRobinStatus,
    autoPlayBalanceStatus: bayAutoPlayBalanceStatus,
    readiness: bayPlayerReadiness, confirmationBypassEnabled, confirmBeforeEachTurnEnabled
  } = bayPlayerProjection;
  const bayPlayerProjectionReady = bayPlayerProjectionStatus === 'ready';
  const applyBayPlayerProjection = useCallback((snapshot: BayPlayerProjectionSnapshot) => {
    dispatchBayPlayerProjection({type: 'apply-snapshot', snapshot});
  }, []);
  const resetBayPlayerProjection = useCallback((status: Extract<BayPlayerProjectionStatus, 'idle' | 'restoring'>) => {
    dispatchBayPlayerProjection({type: 'reset', status});
  }, []);
  const markBayPlayerProjectionError = useCallback(() => {
    dispatchBayPlayerProjection({type: 'mark-error'});
  }, []);
  const [bayPlayerProjectionRetryKey, setBayPlayerProjectionRetryKey] = useState(0);
  const retryBayPlayerProjection = useCallback(() => {
    resetBayPlayerProjection('restoring');
    setBayPlayerProjectionRetryKey(current => current + 1);
  }, [resetBayPlayerProjection]);
  const patchBayPlayerProjection = useCallback((patch: Partial<BayPlayerProjectionState>) => {
    dispatchBayPlayerProjection({type: 'patch', patch});
  }, []);
  const setStationId = useCallback((val: number) => {
    patchBayPlayerProjection({stationId: val});
  }, [patchBayPlayerProjection]);
  const setUserSessionData = useCallback((val: PartnerKioskUpdate_UserSessionUpdateData | undefined) => {
    patchBayPlayerProjection({userSessionData: val});
  }, [patchBayPlayerProjection]);
  const setRoundRobinStatus = useCallback((val: RoundRobinStatus | null) => {
    patchBayPlayerProjection({roundRobinStatus: val});
  }, [patchBayPlayerProjection]);
  const setConfirmationBypassEnabled = useCallback((enabled: boolean) => {
    patchBayPlayerProjection({confirmationBypassEnabled: enabled});
  }, [patchBayPlayerProjection]);
  const setConfirmBeforeEachTurnEnabled = useCallback((enabled: boolean) => {
    patchBayPlayerProjection({confirmBeforeEachTurnEnabled: enabled});
  }, [patchBayPlayerProjection]);
  const [bayPlayerJwt, setBayPlayerJwtState] = useState('');
  const [bayPlayerCredentialStatus, setBayPlayerCredentialStatus] = useState<BayPlayerCredentialStatus>('hydrating');
  const bayPlayerCredentialOperationRef = useRef(0);

  const hydrateBayPlayerCredential = useCallback(async (): Promise<void> => {
    const operationId = ++bayPlayerCredentialOperationRef.current;
    setBayPlayerCredentialStatus('hydrating');

    try {
      const jwt = await readBayPlayerJwt();
      if (operationId !== bayPlayerCredentialOperationRef.current) return;

      if (jwt) {
        setBayPlayerJwtState(jwt);
        resetBayPlayerProjection('restoring');
        setBayPlayerCredentialStatus('ready');

      } else {
        setBayPlayerJwtState('');
        resetBayPlayerProjection('idle');
        setBayPlayerCredentialStatus('missing');
      }

    } catch {
      if (operationId !== bayPlayerCredentialOperationRef.current) return;
      setBayPlayerJwtState('');
      resetBayPlayerProjection('idle');
      setBayPlayerCredentialStatus('error');
    }
  }, [resetBayPlayerProjection]);

  // A non-empty credential becomes visible to consumers only after durable
  // persistence succeeds. Clearing is deliberately ordered the other way:
  // local auth state must be revoked immediately even if device storage is
  // unavailable. persistBayPlayerJwt reports sanitized telemetry and throws a
  // safe, user-facing error for callers to handle.
  const setBayPlayerJwt = useCallback(async (jwt: string): Promise<void> => {
    const operationId = ++bayPlayerCredentialOperationRef.current;
    setBayPlayerCredentialStatus('hydrating');

    if (jwt) {
      try {
        await persistBayPlayerJwt(jwt);
        if (operationId !== bayPlayerCredentialOperationRef.current) return;
        setBayPlayerJwtState(jwt);
        resetBayPlayerProjection('restoring');
        setBayPlayerCredentialStatus('ready');

      } catch (error) {
        if (operationId === bayPlayerCredentialOperationRef.current) {
          setBayPlayerJwtState('');
          resetBayPlayerProjection('idle');
          setBayPlayerCredentialStatus('error');
        }
        throw error;
      }

      return;
    }

    setBayPlayerJwtState('');
    resetBayPlayerProjection('idle');
    try {
      await persistBayPlayerJwt('');
      if (operationId === bayPlayerCredentialOperationRef.current) setBayPlayerCredentialStatus('missing');

    } catch (error) {
      if (operationId === bayPlayerCredentialOperationRef.current) setBayPlayerCredentialStatus('error');
      throw error;
    }
  }, [resetBayPlayerProjection]);

  const retryBayPlayerCredential = useCallback(async (): Promise<void> => {
    await hydrateBayPlayerCredential();
  }, [hydrateBayPlayerCredential]);
  // FSG-8881: the most recent FullSwing deep-link login inputs, memory-only
  // (the id_token is a credential — deliberately NOT persisted, unlike
  // session_id). Consumed by completeRegistrationJoin to re-run the
  // handshake once a new user finishes registration.
  const partnerLoginCredentials = useRef<{ sessionId: string, idToken: string } | null>(null);
  // SessionBackend slot. Seeded to the default; fullSwingTokenLogin flips it to
  // 'legacy' on fallback, and it's persisted/rehydrated (setSessionBackend + the
  // rehydrate useEffect below).
  const [sessionBackend, setSessionBackendState] = useState<SessionBackend>(DEFAULT_SESSION_BACKEND);
  // Persist the resolved backend to AsyncStorage (mirroring setBayPlayerJwt) so
  // the cold-start rehydrate below restores the right backend — and therefore
  // reopens the right live-session stream — without forcing the user back
  // through login. Both members of the union are non-empty strings, so this is
  // always a write; clearing happens on logout (removeItem + reset to default).
  const setSessionBackend = (val: SessionBackend) => {
    setSessionBackendState(val);
    AsyncStorage.setItem(SESSION_BACKEND_STORAGE_KEY, val).catch((err) => console.log('AsyncStorage.setItem session_backend err', err));
  };

  const [streamError, setStreamError] = useState('');
  // SMG-3446: station-offered wager categories + the player's active
  // category, bridged from SessionPlayer by mergePlayerStreamProjections.
  const [allowedWagerModes, setAllowedWagerModes] = useState<WagerMode[]>([]);
  const [activeWagerMode, setActiveWagerMode] = useState<WagerMode>(WagerMode.NONE);
  // SMG-3446: available USD balance (minor units) for the funds-aware default.
  const [usdAvailableMinor, setUsdAvailableMinor] = useState(0);
  // SMG-3455: server-driven funding-fallback flags (see initTypes doc above).
  const [serverMovedToPointsInsufficientFunds, setServerMovedToPointsInsufficientFunds] = useState(false);
  const [insufficientFundsNoFallback, setInsufficientFundsNoFallback] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [activePlayerConfirmationModalVisible, setActivePlayerConfirmationModalVisible] = useState(0);

  // E2E test state injection — only active when ?e2e=1 query param present.
  //
  // SAFETY: this Provider owns the Context-level injections only:
  //   - __TEST_ROUND_ROBIN_STATUS__       → roundRobinStatus
  //   - __TEST_CONFIRMATION_BYPASS__      → confirmationBypassEnabled
  //   - __TEST_CURRENT_USER_ID__          → user (minimal-cast — see note below)
  //
  // activePlayerConfirmationModalVisible is published by
  // useActivePlayerConfirmationFlow. The development-only E2E injection is
  // handled by that controller alongside persistence recovery.
  //
  // Build-time gate: the entire branch is dead-code in production bundles.
  // Metro/webpack/Vite resolve `process.env.NODE_ENV !== 'production'` to
  // `false` at build time when NODE_ENV is set, and tree-shake the body —
  // so the test injection cannot reach a shipped build even if a window
  // global were somehow set. Belt-and-braces: the runtime ?e2e=1 check
  // remains in case a non-production build ever reaches an end user.
  // Cold-start recovery: restore bay-session player JWT from
  // AsyncStorage on mount so a hard refresh / app re-open keeps the
  // existing bay-session session live without forcing the user back
  // through PartnerAuth. The handshake will re-fire on the next
  // fullSwingTokenLogin (URL deep-link, manual login), but for the
  // common "refresh while playing" case the persisted JWT bridges
  // the gap so StreamPlayerState reconnects without an auth-empty
  // round-trip.
  useEffect(() => {
    void hydrateBayPlayerCredential();
  }, [hydrateBayPlayerCredential]);

  // Cold-start rehydrate: restore the persisted backend so a refresh / re-open
  // resumes the same live-session stream without forcing the user back through
  // login.
  useEffect(() => {
    AsyncStorage.getItem(SESSION_BACKEND_STORAGE_KEY).then((stored) => {
      // Strict allowlist: only the two known union members are accepted, so a
      // corrupt / unexpected persisted value can never mis-route the stream
      // facade. Anything else leaves the seeded DEFAULT_SESSION_BACKEND in
      // place.
      if (stored === 'bay-session' || stored === 'legacy') {
        setSessionBackendState(stored);
      }
    }).catch((err) => console.log('AsyncStorage.getItem session_backend err', err));
  }, []);

  // Legacy sessions may still rely on a station id persisted by older app
  // versions. Keep that compatibility hydration here, after the backend has
  // resolved to legacy, instead of letting MenuModal inject the value into a
  // bay-session flow before its authoritative snapshot arrives.
  useEffect(() => {
    if (sessionBackend !== 'legacy') return;

    let cancelled = false;
    readPersistedLegacyStationId().then((persistedStationId) => {
      if (!cancelled && persistedStationId !== null) {
        setStationId(persistedStationId);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [sessionBackend]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location?.search || '');
      if (params.get('e2e') === '1') {
        if ((window as any).__TEST_ROUND_ROBIN_STATUS__) {
          setRoundRobinStatus((window as any).__TEST_ROUND_ROBIN_STATUS__);
        }
        if ((window as any).__TEST_CONFIRMATION_BYPASS__ !== undefined) {
          setConfirmationBypassEnabled((window as any).__TEST_CONFIRMATION_BYPASS__);
        }
        const testUserId = (window as any).__TEST_CURRENT_USER_ID__;
        if (testUserId !== undefined && testUserId !== null) {
          // Construct a minimal User object — only userId / username are read by
          // the RR components ("is it my turn?" via String(user.userId)). The
          // rest of the fields stay as their proto3 zero values via `as User`.
          // BigInt() accepts both number and string forms, matching the loose
          // typing on window.__TEST_CURRENT_USER_ID__ in e2e fixtures.
          //
          // SAFETY: This injection is bounded to round-robin e2e fixtures.
          // Other User fields (dateOfBirth, autoPlayEnabled, firstname, etc.)
          // are NOT populated — the `as User` cast lies about their presence.
          // Do NOT extend e2e tests using this injection path to navigate to
          // Account or BetSlip without first building a fuller fixture User.
          // Track at SMG-2700 if the wider coverage becomes necessary.
          setUser({userId: BigInt(testUserId), username: `TestUser${testUserId}`} as User);
        }
      }
    }
  }, []);

  const fetchBalanceSnapshot = async (localSession?: Session): Promise<BalanceSnapshot> => {
    const request = create(GetBalanceRequestSchema, {token: localSession?.token || session?.token || ''});
    const res = await client.getBalance(request);
    const {success, balance: nextBalance, error, totalBonusesBalance} = res;

    if (!success) throw error ?? {message: 'We could not load your balance. Please try again.'};

    return {balance: Number(nextBalance), totalBonuses: Number(totalBonusesBalance)};
  };

  const applyBalanceSnapshot = (snapshot: BalanceSnapshot): void => {
    setTotalBonuses(snapshot.totalBonuses);
    setBalance(snapshot.balance);
  };

  const fetchUserSnapshot = async (localSession?: Session): Promise<UserSnapshot> => {
    const request = create(GetUserRequestSchema, {token: localSession?.token || session?.token || ''});
    return createUserSnapshot(await client.getUser(request));
  };

  const applyUserSnapshot = (snapshot: UserSnapshot): void => {
    setExtendedUserData(snapshot.extendedUserData);
    setUser(snapshot.user);
    registerMaxShotAmount(snapshot.maxShotAmount);
  };

  const getBalance = async (localSession?: Session): Promise<number | Error> => {
    try {
      const snapshot = await fetchBalanceSnapshot(localSession);
      applyBalanceSnapshot(snapshot);
      return snapshot.balance;

    } catch (err) {
      console.log('getBalance', err);
      return err as Error;
    }
  };

  const logout = async () => {
    // FSG-8815 reopen: arm the PartnerAuth sign-out gate FIRST, before any
    // teardown that could throw. The post-logout navigation.reset lands on
    // PartnerAuth, whose auto-auth recovers the original QR deep-link
    // credentials (react-native-web serves the page's ORIGINAL load URL from
    // Linking.getInitialURL() forever) and would re-auth + re-join the player
    // the instant they signed out. Arming also scrubs id_token/session_id
    // from the address bar so the sign-out handlers' window.location.reload()
    // cannot re-auth from the URL either. In-memory by design: a genuine new
    // QR scan is a fresh page load and is never blocked.
    if (isFullSwing) markExplicitPartnerSignOut();
    // SMG-4069: an explicit sign-out also drops the involuntary-removal
    // gate — the player is deliberately out, so the next scan of the same
    // session should log in without the Rejoin prompt. Best-effort: the
    // leaveSession below can race a PLAYER_LEFT frame back into the marker,
    // which is benign (worst case is one extra "Rejoin" tap on the next
    // scan of the SAME session; the sign-out gate already scrubbed the URL
    // credentials, so no silent re-auth is possible either way).
    if (isFullSwing) clearPartnerRemovalGate().catch(() => {});

    const isBaySessionFullSwing = isFullSwing && sessionBackend === 'bay-session';
    let logoutBayPlayerJwt = isBaySessionFullSwing ? bayPlayerJwt : '';

    if (isBaySessionFullSwing && !logoutBayPlayerJwt) {
      try {
        logoutBayPlayerJwt = (await readBayPlayerJwt()) ?? '';

      } catch {
        // The storage boundary already reports sanitized telemetry. Logout
        // remains best-effort and must still complete its local teardown.
      }
    }

    try {
      let acceptedBetId = isBaySessionFullSwing ? toPositiveBetId(bayActiveBetId) : undefined;

      // bayActiveBetId is stream-authoritative, but idle/cold-start logout can
      // run before the first PlayerState frame. The legacy read remains a
      // discovery fallback only; bay-session still owns the state transition.
      if (!acceptedBetId) {
        const betsList = await ServiceClient.getBets('accepted');
        const activeBet = betsList?.find(el => el.betType === 'paytable');
        acceptedBetId = toPositiveBetId(activeBet?.betId);
      }

      if (acceptedBetId) {
        if (isBaySessionFullSwing) {
          if (logoutBayPlayerJwt) {
            await ServiceClient.cancelBetV2(acceptedBetId, logoutBayPlayerJwt);

          } else {
            track('bay_session_logout_cleanup_skipped', {operation: 'cancel_bet', reason: 'missing_player_jwt'});
          }

        } else {
          await ServiceClient.refundBet(acceptedBetId);
        }
      }

    } catch (e) {
      console.log('logout bet cancellation error', e);
    }

    // FullSwing checkout: release the player from the session the game is
    // rendering. WHICH checkout depends on which of our backends manages the
    // station — recorded at login as sessionBackend:
    //
    //   - 'bay-session': PlayerBaySessionService.LeaveSession. Server-side it
    //     removes the player from the bay-session roster and, for rotation
    //     sessions, from the turn order. Authenticated with the bay-session
    //     player JWT; the handler enforces act-on-self, so the request
    //     user_id must equal the JWT's resolved user.
    //
    //   - 'legacy' (partner-service-managed station — what the deployed FS
    //     Golf fleet runs until the 2026-08 bay-session cutover): the roster
    //     the game renders lives in partner-service
    //     (fs_station_session_participants), which LeaveSession never
    //     touches — and the fallback login cleared bayPlayerJwt anyway, so
    //     the bay branch cannot fire. Drive the partner-service
    //     FullSwingCheckOut exactly as the pre-BSA logout did. Skipping this
    //     left the player on the game's screen after app logout (FSG-8815).
    if (isFullSwing) {
      const checkoutStationId = BigInt(stationId || 0);
      const checkoutUserId = user?.userId ?? 0n;

      try {
        // Both branches are best-effort: a failed checkout must not strand
        // the user mid-logout.
        if (sessionBackend === 'legacy') {
          // session_id (the FullSwing-supplied session UUID) was persisted
          // at login; it is the checkout key on the partner-service path.
          const partnerSessionId = await AsyncStorage.getItem(FULL_SWING_SESSION_ID_STORAGE_KEY);
          if (partnerSessionId) {
            // Idle-expiry and cold-start logouts can reach here before the
            // session state (and the ServiceClient sync effect) is hydrated
            // — the screens' lastActiveTime checks call logout() directly.
            // An empty token makes the checkout a guaranteed server-side
            // no-op (GetUser('') fails) and the player would stay on the
            // game's roster — the FSG-8815 symptom, back again on the
            // walk-away path. Hydrate from the persisted session instead.
            if (!ServiceClient.session?.token) {
              const storedSession = await AsyncStorage.getItem('session').catch(() => null);
              if (storedSession) {
                try {
                  ServiceClient.session = JSON.parse(storedSession);
                } catch {
                  // Corrupt persisted session — fall through; the RPC will
                  // fail and be swallowed like any other checkout failure.
                }
              }
            }
            await ServiceClient.fullSwingCheckOut(partnerSessionId);
          }
        } else if (logoutBayPlayerJwt && checkoutStationId !== 0n && checkoutUserId !== 0n) {
          // Attempted only when we have a player JWT and a resolved station
          // + user. A missing station id (the per-user stream hasn't
          // populated it yet) or an absent JWT degrades to a local-only
          // logout rather than a guaranteed-to-fail RPC.
          const request = create(LeaveSessionRequestSchema, {stationId: checkoutStationId, userId: checkoutUserId});

          await PlayerBaySession_Client.leaveSession(request, {headers: {Authorization: `Bearer ${logoutBayPlayerJwt}`}});

        } else if (!logoutBayPlayerJwt && checkoutStationId !== 0n && checkoutUserId !== 0n) {
          track('bay_session_logout_cleanup_skipped', {operation: 'leave_session', reason: 'missing_player_jwt'});
        }

      } catch (e) {
        console.log('logout error', e);

      } finally {
        // Clear the persisted session UUID on EVERY FullSwing logout —
        // whether the checkout succeeded, failed, or was skipped — so a
        // stale id can't bleed into the next session. The finally guarantees
        // this even when the RPC above rejects.
        await AsyncStorage.removeItem(FULL_SWING_SESSION_ID_STORAGE_KEY).catch(err => console.log('logout session_id cleanup error', err));
      }
    }

    await AsyncStorage.removeItem('session');

    setUser(undefined);
    setSession(undefined);

    try {
      await setBayPlayerJwt('');

    } catch {
      // The storage boundary already emitted sanitized telemetry. The setter
      // cleared memory + projection before attempting removal, so logout must
      // continue instead of trapping the user in an authenticated UI.
    }

    // FSG-8881: drop the retained FullSwing deep-link credentials (incl.
    // the id_token) so they can't linger in memory across users on a
    // shared bay tablet. A fresh login re-populates the ref before any
    // completeRegistrationJoin can read it.
    partnerLoginCredentials.current = null;
    // Reset the backend to the canonical default and drop the persisted copy so
    // the next cold start doesn't resume a stale 'legacy' (or 'bay-session')
    // routing for a fresh login. Mirrors the bay_player_jwt cleanup above.
    setSessionBackendState(DEFAULT_SESSION_BACKEND);
    AsyncStorage.removeItem(SESSION_BACKEND_STORAGE_KEY).catch(() => undefined);
    setBalance(0);
    setExtendedUserData({
      maxShotAmount: 0, hasActiveTournament: false, freeShotCount: 0, freeShotNumber: 0, completedFreeShots: 0,
      isNewPlayer: false, hasNewBonus: false, tournamentAutoPlayAmount: 0, skillIndex: 0
    });
    // The level-up latch is per-user: without this reset, the next login on
    // the same kiosk device compares against the PREVIOUS user's max and
    // fires a spurious "Level Up" when the new user's max is simply higher.
    knownMaxShotAmountRef.current = 0;

    resetUser();

    return true;
  };

  const imageLogin = async (image: string) => {
    const bytes = Uint8Array.from(Buffer.from(image, 'base64'));
    const request = create(ImageLoginRequestSchema, {image: bytes});

    return client.imageLogin(request).then(res => {
      const {success, user: resUser, session: resSession, error} = res;

      if (success && resSession && resUser) {
        assertLoginEligibleOrThrow(resUser.dateOfBirth, resSession.token);
        setSession(resSession);
        getBalance(resSession).then();
        AsyncStorage.setItem('session', JSON.stringify(resSession));
        setUser(resUser);
        identifyUser(String(resUser.userId));
        return success;

      }

      throw error;
    });
  };

  const fullSwingTokenLogin = async (session_id: string, id_token: string) => {
    // FSG-8881: retain the deep-link inputs IN MEMORY ONLY (the id_token is a
    // credential — never persist it) so completeRegistrationJoin can re-run
    // this handshake after a new user finishes registration. The server
    // defers the implicit session join while tnc_accepted=false; the re-run
    // performs it.
    partnerLoginCredentials.current = {sessionId: session_id, idToken: id_token};

    // session_id is the FullSwing-supplied UUID from the deep-link, not a
    // credential, so it's safe to persist before the auth gates.
    await AsyncStorage.setItem(FULL_SWING_SESSION_ID_STORAGE_KEY, session_id).catch(() => {
      console.error('[FullSwingAuth] session identifier persistence failed');
    });

    // Try the canonical bay-session login first; fall back to legacy ONLY when
    // the station is not bay-session-managed. Every other error (user-miss, bad
    // JWT, smg-core down) surfaces with no fallback — the whitelist is exactly
    // one signal. The chosen backend is recorded so the stream facade and
    // cold-start rehydrate resume the matching stream.
    try {
      try {
        return await fullSwingTokenLoginBaySession(session_id, id_token);

      } catch (error) {
        if (!isStationNotBaySessionManaged(error)) {
          throw error;
        }
        // Track the backend switch: a surge of fallbacks (e.g. a server
        // misconfiguring which stations are bay-session-managed) would otherwise
        // be invisible. Non-PII — decision + reason only.
        track('fullswing_login_backend_fallback', {reason: 'station_not_bay_session_managed'});
        return await fullSwingTokenLoginLegacy(session_id, id_token);
      }

    } catch (error) {
      const normalizedError = normalizeAuthError(error, 'sign-in');
      reportAuthFailure('fullswing_login_failed', normalizedError, 'sign-in');
      throw normalizedError;
    }
  };

  // FSG-8881: after registration (profile + T&C) completes, re-run the
  // FullSwing handshake so the server performs the join it deferred while
  // tnc_accepted was false. Idempotent server-side (already-joined
  // short-circuit), and it refreshes the session/user state so the new
  // screen name is live before the app navigates into the session. When
  // the in-memory credentials are gone (mid-registration page refresh),
  // resolve without joining — the user re-enters via the QR deep link,
  // which now joins on that login because tnc is accepted.
  const completeRegistrationJoin = async (): Promise<void> => {
    const creds = partnerLoginCredentials.current;
    if (!creds) {
      // Emit telemetry, not just a console.warn: this is the "registered
      // but never joined the roster" hole (in-memory creds dropped by a
      // mid-registration reload). Without a metric a prod spike is
      // invisible; the user's recovery is an undiscoverable QR re-scan.
      console.warn('completeRegistrationJoin: no FullSwing login credentials in memory; skipping deferred join');
      track('fullswing_deferred_join_skipped', {reason: 'no_credentials_in_memory'});
      return;
    }

    try {
      await fullSwingTokenLogin(creds.sessionId, creds.idToken);

    } catch (error) {
      // The re-auth (which performs the deferred join) failed — e.g. the
      // initial id_token expired during a long registration, or a
      // transient RPC error. Surface it in telemetry so the missed-join
      // rate is measurable; re-throw so the caller keeps its existing
      // best-effort logging + navigation.
      const normalizedError = normalizeAuthError(error, 'deferred-join');
      reportAuthFailure('fullswing_deferred_join_failed', normalizedError, 'deferred-join');
      throw normalizedError;
    }
  };

  const clearFullSwingAuthContext = (credentialCleanupFailed: boolean): void => {
    ++bayPlayerCredentialOperationRef.current;
    setBayPlayerJwtState('');
    setBayPlayerCredentialStatus(credentialCleanupFailed ? 'error' : 'missing');
    resetBayPlayerProjection('idle');
    setSession(undefined);
    setUser(undefined);
    setSessionBackendState(DEFAULT_SESSION_BACKEND);
    setBalance(0);
    setTotalBonuses(0);
    setExtendedUserData({
      maxShotAmount: 0, hasActiveTournament: false, freeShotCount: 0, freeShotNumber: 0, completedFreeShots: 0,
      isNewPlayer: false, hasNewBonus: false, tournamentAutoPlayAmount: 0, skillIndex: 0
    });
    setAllowedWagerModes([]);
    setActiveWagerMode(WagerMode.NONE);
    setUsdAvailableMinor(0);
    setServerMovedToPointsInsufficientFunds(false);
    setInsufficientFundsNoFallback(false);
    knownMaxShotAmountRef.current = 0;
    ServiceClient.session = {token: ''} as Session;

    try {
      resetUser();
    } catch {
      console.error('[FullSwingAuth] analytics identity reset failed');
    }
  };

  // Shared prepare/persist/publish transaction for every FullSwing auth path.
  // User data is required; balance is best-effort so a wallet read outage does
  // not turn a valid authentication into a false login failure. No new Context
  // state is published until every required storage write succeeds.
  const commitFullSwingSession = async (resSession: Session, backend: SessionBackend, nextBayPlayerJwt: string): Promise<User> => {
    const credentialOperationId = ++bayPlayerCredentialOperationRef.current;
    setBayPlayerCredentialStatus('hydrating');
    let phase: 'prepare' | 'persist' = 'prepare';

    try {
      const balancePromise = fetchBalanceSnapshot(resSession).catch(() => {
        try {
          track('fullswing_auth_balance_refresh_failed', {backend});
        } catch {
          console.error('[FullSwingAuth] balance telemetry reporting failed', {backend});
        }
        return null;
      });
      const [userSnapshot, balanceSnapshot, legacyStationId] = await Promise.all([
        fetchUserSnapshot(resSession),
        balancePromise,
        backend === 'legacy' ? readPersistedLegacyStationId() : Promise.resolve(null),
      ]);

      if (credentialOperationId !== bayPlayerCredentialOperationRef.current) {
        throw new SafeAuthError({
          category: 'cancelled', code: 'credential_operation_superseded', retryable: true,
          userMessage: AUTH_ERROR_MESSAGES.authenticationCancelled
        });
      }

      const serializedSession = JSON.stringify(resSession);
      phase = 'persist';
      await persistFullSwingAuthSession({
        serializedSession, lastActiveTime: moment().format(), backend, bayPlayerJwt: nextBayPlayerJwt
      });

      if (credentialOperationId !== bayPlayerCredentialOperationRef.current) {
        throw new SafeAuthError({
          category: 'cancelled', code: 'credential_operation_superseded', retryable: true,
          userMessage: AUTH_ERROR_MESSAGES.authenticationCancelled
        });
      }

      // Publish the complete in-memory state only after durable persistence.
      setBayPlayerJwtState(nextBayPlayerJwt);
      setBayPlayerCredentialStatus(backend === 'bay-session' ? 'ready' : 'missing');
      resetBayPlayerProjection(backend === 'bay-session' ? 'restoring' : 'idle');
      if (legacyStationId !== null) setStationId(legacyStationId);
      setSession(resSession);
      setSessionBackendState(backend);
      knownMaxShotAmountRef.current = 0;
      applyUserSnapshot(userSnapshot);
      if (balanceSnapshot) {
        applyBalanceSnapshot(balanceSnapshot);
      } else {
        // Never carry the previous player's financial state into a new login.
        setBalance(0);
        setTotalBonuses(0);
      }
      ServiceClient.session = resSession;

      if (backend === 'bay-session') {
        // This key belongs to the retired pre-BSA station picker. It is not part
        // of the auth transaction, so cleanup remains best-effort.
        AsyncStorage.removeItem(LEGACY_STATION_ID_STORAGE_KEY).catch(() => undefined);
      }

      try {
        identifyUser(String(userSnapshot.user.userId));
      } catch {
        console.error('[FullSwingAuth] analytics identity commit failed');
      }
      return userSnapshot.user;

    } catch (error) {
      const failedCleanupOperations = await clearFullSwingAuthSession();
      clearFullSwingAuthContext(failedCleanupOperations.length > 0);

      try {
        track('fullswing_auth_commit_failed', {backend, phase});
      } catch {
        console.error('[FullSwingAuth] commit telemetry reporting failed', {backend, phase});
      }
      throw error;
    }
  };

  // Canonical bay-session login. The handler verifies the FullSwing JWT,
  // resolves/provisions the user, fail-closed-logs in against smg-core, and
  // returns both the bay-session player JWT and the smg session token.
  const fullSwingTokenLoginBaySession = async (session_id: string, id_token: string): Promise<User> => {
    const bayReq = create(FullSwingAuthRequestSchema, {fullswingJwt: id_token, sessionId: session_id});
    const bayRes = await PlayerBaySession_Client.fullSwingAuth(bayReq);

    // Gate everything before any persistent side effect: persisting the JWT
    // before the age/token checks would leave an under-18 user with a working
    // credential the cold-start rehydrate restores on next launch. A missing
    // JWT is a hard auth failure, so it's checked first; the eligibility gates
    // then run on the authenticated response. Do not reorder.
    if (!bayRes.evenplayJwt) {
      throw new SafeAuthError({
        category: 'invalid_credentials', code: 'missing_bay_credential', retryable: true,
        userMessage: AUTH_ERROR_MESSAGES.authenticationFailed
      });
    }
    assertLoginEligibleOrThrow(bayRes.dateOfBirth, bayRes.smgSessionToken);

    // Plain-object cast rather than create(SessionSchema): protobuf-es v2
    // defaults bigint fields to 0n, which JSON.stringify can't serialize for
    // the AsyncStorage persist. Downstream consumers read only `.token`.
    const resSession = {token: bayRes.smgSessionToken} as Session;
    return commitFullSwingSession(resSession, 'bay-session', bayRes.evenplayJwt);
  };

  // Legacy fallback for stations that are not bay-session-managed. This path
  // predates the eligibility gates, so it MUST run the same ones as the bay
  // branch before persisting — otherwise an under-18 (or token-less) user could
  // bypass the check here and survive an app restart via the rehydrate.
  const fullSwingTokenLoginLegacy = async (session_id: string, id_token: string): Promise<User> => {
    const legacyReq = create(FullSwingTokenLoginRequestSchema, {idToken: id_token, sessionId: session_id});
    const res = await Partner_Client.fullSwingTokenLogin(legacyReq);

    if (!res.success || !res.session || !res.user) {
      throw res.error ?? {message: 'Authentication failed — please try again.'};
    }
    assertLoginEligibleOrThrow(res.user.dateOfBirth, res.session.token);
    const currentUser = await commitFullSwingSession(res.session, 'legacy', '');

    // Join the player into the session. Unlike bay-session FullSwingAuth (which
    // implicit-joins), the legacy partner-service FullSwingTokenLogin only
    // authenticates — the actual session join (JoinPlayer + round-robin) was
    // always a separate FullSwingCheckIn call (dropped from PartnerAuth at the
    // BSA cutover precisely because the bay path no longer needs it). The legacy
    // fallback still does, or the player authenticates but never enters the
    // session. Best-effort, matching the pre-cutover flow: a join failure is
    // logged but does not fail the login (the session token is already valid).
    // ServiceClient.session is set explicitly here because setSession only
    // propagates to ServiceClient on the next render (see the effect below).
    //
    // FSG-8881: gated on tncAccepted, restoring the pre-BSA PartnerAuth
    // behavior — a mid-registration user must not land on the game's roster.
    // completeRegistrationJoin re-runs this login after registration, and
    // tncAccepted is true on that pass.
    ServiceClient.session = res.session;
    if (currentUser.tncAccepted) {
      await ServiceClient.fullSwingCheckIn(session_id).catch(error => {
        reportAuthFailure('legacy_fullswing_session_join_failed', error, 'session-join');
      });
    }
    return currentUser;
  };

  // Native evenplay self-check-in. Same fail-closed ordering as
  // fullSwingTokenLoginBaySession: verify the credential server-side, gate on
  // an empty JWT / failed eligibility check BEFORE any persistent side
  // effect, then persist + commit. The server-side handler is still a stub
  // returning Unimplemented (ships separately); this client path is real.
  const evenPlayAuth = async ({credential, sessionId, promoType}: EvenPlayAuthParams): Promise<User> => {
    try {
      const credentialField = credential.kind === 'otp' ? {
        case: 'otp' as const,
        value: create(OtpCredentialSchema, {
          otpCode: credential.code,
          contact: credential.contact.method === 'email'
            ? {case: 'emailAddress' as const, value: credential.contact.value}
            : {case: 'phoneNumber' as const, value: credential.contact.value}
        })
      } : {
        case: 'image' as const,
        value: create(ImageCredentialSchema, {image: Uint8Array.from(Buffer.from(credential.imageBase64, 'base64'))})
      };

      const req = create(EvenPlayAuthRequestSchema, {
        credential: credentialField, sessionId,
        promoType: promoType || '',
        // Client-generated per attempt, same pattern as acceptBetV2's
        // acceptIdempotencyKey (ServiceClient.ts): Crypto.randomUUID(), no
        // polyfill required. A retry of a rejected submission calls
        // evenPlayAuth again and gets a fresh key, which is fine — the key
        // only needs to make ONE attempt at consuming a given OTP code
        // retry-safe, not to be stable across separate user-initiated
        // submissions.
        idempotencyKey: Crypto.randomUUID(),
      });
      const res = await PlayerBaySession_Client.evenPlayAuth(req);

      // Gate everything before any persistent side effect — identical reasoning
      // to fullSwingTokenLoginBaySession: persisting the JWT before the
      // age/token checks would leave an under-18 (or token-less) user with a
      // working credential the cold-start rehydrate restores on next launch.
      if (!res.evenplayJwt) {
        throw new SafeAuthError({
          category: 'invalid_credentials', code: 'missing_bay_credential', retryable: true,
          userMessage: AUTH_ERROR_MESSAGES.authenticationFailed
        });
      }
      assertLoginEligibleOrThrow(res.dateOfBirth, res.smgSessionToken);

      // Plain-object cast rather than create(SessionSchema): see
      // fullSwingTokenLoginBaySession for why (bigint defaults break JSON
      // serialization for the AsyncStorage persist).
      const resSession = {token: res.smgSessionToken} as Session;
      return await commitFullSwingSession(resSession, 'bay-session', res.evenplayJwt);

    } catch (error) {
      const normalizedError = normalizeAuthError(error, 'sign-in');
      reportAuthFailure('evenplay_auth_failed', normalizedError, 'sign-in');
      throw normalizedError;
    }
  };

  const otpLogin = async (email: string, code: string) => {
    const request = create(OtpLoginRequestSchema, {emailAddress: email, otpCode: code});

    return client.otpLogin(request).then(async (res) => {
      const {success, error, session: resSession, user: resUser} = res;

      if (success && resSession) {
        assertLoginEligibleOrThrow(resUser?.dateOfBirth, resSession.token);
        setSession(resSession);
        await getBalance(resSession);
        const currentUser = await getUser(resSession);
        AsyncStorage.setItem('session', JSON.stringify(resSession)).then();
        identifyUser(String(currentUser.userId));
        return success;

      }

      throw error;
    });
  };

  const emailLogin = async (email: string, password: string) => {
    const request = create(EmailLoginRequestSchema, {emailAddress: email, password});

    return client.emailLogin(request).then(res => {
      const {error, session: resSession, user: resUser, success} = res;

      if (success && resSession && resUser) {
        assertLoginEligibleOrThrow(resUser.dateOfBirth, resSession.token);
        setSession(resSession);
        getBalance(resSession).then();
        AsyncStorage.setItem('session', JSON.stringify(resSession));
        setUser(resUser);
        identifyUser(String(resUser.userId));
        return success;

      }

      throw error;
    });
  };

  const registerUser = async (values: registerUserPayloadType) => {
    const {
      email, smsCode, phone, username, handedness, handicap, dateOfBirth, firstname, lastname, preferredOtpMethod,
      gender
    } = values;

    const request = create(RegisterUserRequestSchema, {
      emailAddress: email, username, otpCode: smsCode, phoneNumber: phone, handedness, handicap,
      dateOfBirth: dateOfBirth ? dateOfBirth.format(dateFormat) : '', firstname, lastname, preferredOtpMethod, gender
    });

    return client.registerUser(request).then(async res => {
      const {success, error, session: resSession} = res;

      if (success && resSession) {
        AsyncStorage.setItem('session', JSON.stringify(resSession));
        setSession(resSession);
        // Best-effort post-register hydration. The account is already committed
        // server-side; if getUser fails (transient network/server issue) we must
        // still resolve so the caller doesn't show "registration failed" for an
        // account that exists. Analytics identification resumes on next login.
        try {
          const currentUser = await getUser(resSession);
          identifyUser(String(currentUser.userId));

        } catch (e) {
          console.log('post-register hydration failed (account still created)', e);
        }

        return resSession;

      } else throw error;
    });
  };

  const getUser = async (localSession?: Session) => {
    const snapshot = await fetchUserSnapshot(localSession);
    // Preserve the public getUser contract: profile refresh succeeds even when
    // balance refresh fails, and the balance request remains asynchronous.
    void getBalance(localSession);
    applyUserSnapshot(snapshot);
    return snapshot.user;
  };

  useEffect(() => {
    if (session) ServiceClient.session = session;
  }, [isTest, session]);

  /**
   * mergePlayerStreamProjections — merge the autoplay / free-play /
   * skill-index fields the bay-session per-user PlayerStateUpdate stream
   * surfaces (via SessionPlayer) into Context.user + Context.extendedUserData.
   *
   * Replaces the BetSlip 10s `Context.getUser` poll: SessionPlayer carries
   * the same fields the popup-gate effect at Main.tsx reads
   * (autoPlayBalanceStatus / autoPlayAmount / autoPlayEnabled) plus the
   * extended-user-data fields (isNewPlayer / freeShotCount / skillIndex)
   * the home-page / BetSlip surface. Stream-driven means sub-second
   * latency instead of the 10s polling cadence.
   *
   * Bay-session's AutoPlayBalanceStatus enum shares integer values with
   * smg-core's AutoPlayBalanceStatus by design (per SMG-3075 proto
   * comment); the TypeScript enums are distinct types so a numeric cast
   * bridges the boundary.
   *
   * No-op when user / sessionPlayer is missing — the initial
   * Context.getUser fetch (on login) seeds the User object with identity
   * fields the stream doesn't carry (firstname, email, tncAccepted,
   * dateOfBirth, kycStatus). The stream then keeps the live-mutable
   * autoplay/free-play/skill fields fresh.
   */
  const mergePlayerStreamProjections = useCallback((state: PlayerState | undefined) => {
    const sp = state?.sessionPlayer;
    if (!sp) return;

    setUser(prev => prev ? mergeStreamUserProjection(prev, sp) : prev);
    // usd_max_wager_cents (SessionPlayer field 34): the live mid-session
    // source of the user's max bet — this is what fires the "Level Up"
    // popup when a shot_count ladder tier unlocks during play. 0 means
    // "unknown" (older server / projection unpopulated): keep the last
    // getUser-sourced value rather than clobbering it.
    const streamMaxShotAmount = Number(sp.usdMaxWagerCents ?? 0n);
    setExtendedUserData(prev => mergeStreamExtendedUserProjection(prev, sp));
    if (streamMaxShotAmount > 0) registerMaxShotAmount(streamMaxShotAmount);
    // SMG-3446: bridge the station-offered wager categories + the
    // player's current category onto Context so the mobile currency
    // picker (Real Money vs Points) can apply the single-availability
    // rule and reflect server truth. Replace-not-merge: the wire array
    // is authoritative for what the station offers right now. The
    // setters bail on value-equal updates to avoid re-render storms
    // under the sub-second stream cadence.
    setAllowedWagerModes((prev) => arraysEqual(prev, sp.allowedWagerModes) ? prev : [...sp.allowedWagerModes]);
    setActiveWagerMode((prev) => (prev === sp.wagerMode ? prev : sp.wagerMode));
    // SMG-3446: bridge the active player's available USD balance (minor units)
    // for the currency picker's funds-aware default (Real Money >= $5 vs an
    // auto-move to Points < $5). Source is PlayerState.funds (field 100), the
    // SUBJECT player's own per-currency availability on the per-user
    // StreamPlayerState (populated server-side by enrichPlayerStateFunds) — NOT
    // SessionPlayer.funds (field 32), which is the station/kiosk projection and
    // is empty on this per-user stream. funds is a repeated CurrencyAvailability;
    // pick the USD row's `available` (cents), 0 when absent (proto3 default /
    // wallet projection not wired). The setter bails on a value-equal update to
    // avoid re-render storms under the sub-second stream cadence.
    const usdFunds = state?.funds?.find((f) => f.currency?.toUpperCase() === 'USD');
    const nextUsdAvailable = usdFunds ? Number(usdFunds.available) : 0;
    setUsdAvailableMinor((prev) => (prev === nextUsdAvailable ? prev : nextUsdAvailable));
  }, [registerMaxShotAmount]);

  const providerValue = {
    user, getUser, setSession, registerUser, emailLogin, imageLogin, session, cancelModalConfig, setCancelModalConfig,
    logout, loadingOverlay, setLoadingOverlay, getBalance, balance, setBalance, isSuccessMessageShow, onLine, setOnLine,
    setIsSuccessMessageShow, contestEntryModalConfig, setContestEntryModalConfig, isTest, setIsTest, totalBonuses,
    otpLogin, extendedUserData, setUser, mergePlayerStreamProjections, fullSwingTokenLogin, completeRegistrationJoin,
    evenPlayAuth, bayPlayerJwt, bayPlayerCredentialStatus, setBayPlayerJwt, retryBayPlayerCredential, sessionBackend,
    setSessionBackend, bayPlayerProjectionStatus, bayPlayerProjectionReady, bayPlayerProjectionRetryKey, setStationId,
    applyBayPlayerProjection, markBayPlayerProjectionError, retryBayPlayerProjection, stationId, bayActiveBetId,
    bayActiveBetCurrency, bayActiveBetAmountMinor, bayShotArmedBetId, bayAutoPlayBalanceStatus, setUserSessionData,
    bayPlayerReadiness, streamError, setStreamError, userSessionData, roundRobinStatus,
    setRoundRobinStatus, confirmationBypassEnabled, setConfirmationBypassEnabled, confirmBeforeEachTurnEnabled,
    setAllowedWagerModes, setConfirmBeforeEachTurnEnabled, allowedWagerModes, activeWagerMode, setActiveWagerMode,
    usdAvailableMinor, setUsdAvailableMinor, serverMovedToPointsInsufficientFunds, setInsufficientFundsNoFallback,
    setShowProfileModal, setServerMovedToPointsInsufficientFunds, insufficientFundsNoFallback, showProfileModal,
    activePlayerConfirmationModalVisible, setActivePlayerConfirmationModalVisible
  };

  return (
    <StoreContext.Provider value={providerValue}>
      {children}
      <LevelUpModal visible={showLevelUpModal} onRequestClose={() => setShowLevelUpModal(false)}/>
    </StoreContext.Provider>
  );
};
